import { Col, Row } from 'react-bootstrap';
import { InputBox } from '../form-elements/inputbox.component';
import { Link } from 'react-router-dom';
import { CheckBox } from '../form-elements/checkbox.component';
import { CompanyConfig } from '../../company.config';

interface ConsentFormProps {
  values?: any;
  errors?: any;
  touched?: any;
  handleChange?: any;
  handleBlur?: any;
  setFieldValue?: any;
  isCustomTheme?: string;
  isEmbeddedMazo?: boolean;
}

export const initialValuesConsent = {
  firstName: '',
  lastName: '',
  usCitizen: false,
  usResident: false,
  applicantAgree: false,
  applicantSignature: '',

  coapplicantFirstName: '',
  coapplicantLastName: '',
  coApplicantAgree: false,
  coApplicantSignature: '',
  coApplicantUsCitizen: false,
  coApplicantUsResident: false,
};

export const ConsentForm: React.FC<ConsentFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue: _setFieldValue,
  isCustomTheme,
  isEmbeddedMazo,
}) => {
  return (
    <>
      <Row>
        <Col xs={12}>
          <h3>Authorization</h3>
        </Col>
      </Row>

      {(isCustomTheme || isEmbeddedMazo) && (
        <Row>
          <Col xs={12}>
            <p>
              This form is provided by {CompanyConfig.name} ({CompanyConfig.shortName}), a third-party financial
              services provider. By submitting your information, you acknowledge that it will be sent directly to{' '}
              {CompanyConfig.shortName} for processing. {CompanyConfig.shortName} will share your information with its
              affiliated lenders for the purpose of evaluating financing options related to your inquiry. Submission of
              this form does not constitute a loan approval, or offer of credit. It is intended only to begin the
              process of exploring available financing. If you have any questions about how your information will be
              used, please read our{' '}
              <Link to={CompanyConfig.privacyUrl} title="Privacy Policy" target="_blank">
                Privacy Policy
              </Link>{' '}
              &{' '}
              <Link to={CompanyConfig.termsUrl} title="Terms of Use" target="_blank">
                Terms of Use
              </Link>
              , or contact {CompanyConfig.shortName} directly before submitting the form.
            </p>
          </Col>
        </Row>
      )}

      <Row>
        <Col xs={12}>
          <p>
            By checking below, each of us (both buyer and co-buyer) verify our intent to apply for joint credit
            authorization and have reviewed and agree to the{' '}
            <Link to={CompanyConfig.privacyUrl} title="Privacy Policy" target="_blank">
              privacy policy accessible here
            </Link>
            .
          </p>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <p>
            I certify that the above information stated in this application is true and correct and a complete statement
            of my financial condition.
          </p>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <p>
            I understand that this application will be kept whether or not it is approved. You are authorized to share
            this application with other potential lenders.
          </p>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <p>
            You and any potential or subsequent creditor are authorized to check my credit and my employment history to
            answer questions about your credit experience with me and to disclose credit information to each other. I
            further understand that my application is being submitted to a lender(s).
          </p>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col xs={12} lg={6} className="py-3">
          <div className="container-fluid">
            <Row>
              <Col xs={12}>
                <h4>Buyer</h4>
                <hr />
              </Col>

              <Col xs={12}>
                <CheckBox
                  label="I agree"
                  name="applicantAgree"
                  valueForm={values.applicantAgree}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isValid={touched.applicantAgree && !errors.applicantAgree}
                  isInvalid={touched.applicantAgree && errors.applicantAgree}
                  errorFeedback={errors.applicantAgree}
                />
              </Col>

              <Col xs={12}>
                <CheckBox
                  label="I am a U.S. Citizen"
                  name="usCitizen"
                  valueForm={values.usCitizen}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isValid={touched.usCitizen && !errors.usCitizen}
                  isInvalid={touched.usCitizen && errors.usCitizen}
                  errorFeedback={errors.usCitizen}
                  tooltip="A person who was born in the U.S. or has become a citizen through the naturalization process."
                />

                <CheckBox
                  label="I am a Lawful Permanent Resident"
                  name="usResident"
                  valueForm={values.usResident}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  // isValid={touched.usResident && !errors.usResident}
                  // isInvalid={touched.usResident && errors.usResident}
                  // errorFeedback={""}
                  tooltip="A non-citizen who has been granted authorization to live and work in the U.S. permanently. Also known as a 'green card holder.'"
                />
              </Col>

              <Col xs={12}>
                <InputBox
                  label="Buyer Signature"
                  name="applicantSignature"
                  placeholder={
                    values.firstName && values.lastName
                      ? `Type "${values.firstName} ${values.lastName}"`
                      : "Type the buyer's full name as entered above"
                  }
                  value={values.applicantSignature}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isValid={touched.applicantSignature && !errors.applicantSignature}
                  isInvalid={touched.applicantSignature && errors.applicantSignature}
                  errorFeedback={errors.applicantSignature}
                />
              </Col>
            </Row>
          </div>
        </Col>

        {values.haveCoApplicant === true ? (
          <Col xs={12} lg={6} className="py-3">
            <div className="container-fluid">
              <Row>
                <Col xs={12}>
                  <h4>Co-Buyer</h4>
                  <hr />
                </Col>

                <Col xs={12}>
                  <CheckBox
                    label="I agree"
                    name="coApplicantAgree"
                    // value={true}
                    valueForm={values.coApplicantAgree}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={touched.coApplicantAgree && !errors.coApplicantAgree}
                    isInvalid={touched.coApplicantAgree && errors.coApplicantAgree}
                    errorFeedback={errors.coApplicantAgree}
                  />
                </Col>

                <Col xs={12}>
                  <CheckBox
                    label="I am a U.S. Citizen"
                    name="coApplicantUsCitizen"
                    // value={true}
                    valueForm={values.coApplicantUsCitizen}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={touched.coApplicantUsCitizen && !errors.coApplicantUsCitizen}
                    isInvalid={touched.coApplicantUsCitizen && errors.coApplicantUsCitizen}
                    errorFeedback={errors.coApplicantUsCitizen}
                    tooltip="A person who was born in the U.S. or has become a citizen through the naturalization process."
                  />

                  <CheckBox
                    label="I am a Lawful Permanent Resident"
                    name="coApplicantUsResident"
                    // value={true}
                    valueForm={values.coApplicantUsResident}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={touched.coApplicantUsResident && !errors.coApplicantUsResident}
                    isInvalid={touched.coApplicantUsResident && errors.coApplicantUsResident}
                    errorFeedback={errors.coApplicantUsResident}
                    tooltip="A non-citizen who has been granted authorization to live and work in the U.S. permanently. Also known as a 'green card holder.'"
                  />
                </Col>

                <Col xs={12}>
                  <InputBox
                    label="Co-Buyer Signature"
                    name="coApplicantSignature"
                    placeholder={
                      values.coapplicantFirstName && values.coapplicantLastName
                        ? `Type "${values.coapplicantFirstName} ${values.coapplicantLastName}"`
                        : "Type the co-buyer's full name as entered above"
                    }
                    value={values.coApplicantSignature}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={touched.coApplicantSignature && !errors.coApplicantSignature}
                    isInvalid={touched.coApplicantSignature && errors.coApplicantSignature}
                    errorFeedback={errors.coApplicantSignature}
                  />
                </Col>
              </Row>
            </div>
          </Col>
        ) : null}
      </Row>
    </>
  );
};
